/**
 * CitationChip — inline superscript chip rendered next to a cited claim
 * inside an agent message.
 *
 * Spec: docs/superpowers/specs/2026-05-05-agent-memory-architecture-design.md
 *   §"L5 — Provenance contract"
 *
 * Visual: small superscript-sized chip (matches the agent's footnote
 * gesture). Tooltip shows source type + truncated id. On click:
 *   - external_object → opens the source app (URL provided by the chip
 *     when known; otherwise we fire `onOpenSource` so the host can
 *     resolve via the existing `external_links` URL path).
 *   - page             → calls `onOpenPage` so the host opens the page
 *                        in the chat sidebar (no nav rail).
 *   - agent_memory     → toggles a small inline popover with the
 *                        memory's summary + provenance.
 *   - conversation /
 *     run /
 *     pulse_briefing   → toggles a small inline popover variant
 *                        identifying the source.
 *
 * Visual idiom mirrors `PulseChipSpan` — amber underline, subtle background
 * tint — kept compact (superscript) so it sits inline with prose without
 * breaking the line cadence. Keyboard-accessible: focus-visible outline,
 * Enter/Space activates.
 */

import { useState, useEffect, useRef } from "react";
import type { Citation } from "@yagni/shared";
import { safeExternalUrl } from "../lib/safeUrl.js";

export interface CitationChipProps {
  citation: Citation;
  /** 1-based index for the chip's visible label (e.g. "[1]"). */
  index: number;
  /** Optional pre-resolved URL for `external_object` citations. */
  url?: string | null;
  /** Optional summary text rendered in the popover for non-link citations. */
  summary?: string | null;
  /** Called for `external_object` citations when no `url` is supplied. */
  onOpenSource?: (citation: Citation) => void;
  /** Called for `page` citations to open the page in the chat sidebar. */
  onOpenPage?: (citation: Citation) => void;
}

const POPOVER_LABELS: Record<Citation["sourceType"], string> = {
  external_object: "External source",
  page: "Page",
  agent_memory: "Agent memory",
  conversation: "Conversation",
  run: "Run",
};

export function CitationChip({
  citation,
  index,
  url,
  summary,
  onOpenSource,
  onOpenPage,
}: CitationChipProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement | null>(null);

  // Close the popover on outside click. Only attached while open.
  useEffect(() => {
    if (!popoverOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (e.target instanceof Node && containerRef.current.contains(e.target)) {
        return;
      }
      setPopoverOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [popoverOpen]);

  const truncatedId =
    citation.sourceId.length > 24
      ? `${citation.sourceId.slice(0, 21)}…`
      : citation.sourceId;
  const partition = citation.sourcePartition
    ? `:${citation.sourcePartition}`
    : "";
  const tooltip = `${POPOVER_LABELS[citation.sourceType] ?? citation.sourceType}${partition} · ${truncatedId}`;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (citation.sourceType === "external_object") {
      // The url is agent-supplied via the citation resolver. window.open
      // is not protected by React's href sanitizer, so gate the scheme.
      const safeUrl = safeExternalUrl(url);
      if (safeUrl) {
        window.open(safeUrl, "_blank", "noopener,noreferrer");
      } else if (onOpenSource) {
        onOpenSource(citation);
      } else {
        setPopoverOpen((x) => !x);
      }
      return;
    }
    if (citation.sourceType === "page") {
      if (onOpenPage) {
        onOpenPage(citation);
      } else {
        setPopoverOpen((x) => !x);
      }
      return;
    }
    // agent_memory / conversation / run / pulse_briefing → popover.
    setPopoverOpen((x) => !x);
  };

  return (
    <span
      ref={containerRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        data-testid="citation-chip"
        data-source-type={citation.sourceType}
        data-source-id={citation.sourceId}
        aria-label={`Citation ${index}: ${tooltip}`}
        title={tooltip}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.stopPropagation();
          }
        }}
        style={{
          // Superscript treatment so the chip sits next to a claim
          // without disrupting line height.
          verticalAlign: "super",
          fontSize: "0.7em",
          lineHeight: 1,
          marginLeft: 2,
          padding: "0 4px",
          borderRadius: 3,
          background: "var(--accent-muted)",
          boxShadow: "inset 0 -1px 0 var(--accent-border)",
          color: "var(--accent)",
          fontWeight: 600,
          fontFamily: "var(--font-mono, monospace)",
          border: "none",
          cursor: "pointer",
        }}
        className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-1"
      >
        [{index}]
      </button>
      {popoverOpen && (
        <span
          role="tooltip"
          data-testid="citation-popover"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 30,
            minWidth: 200,
            maxWidth: 320,
            padding: "8px 10px",
            background: "var(--bg-surface)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            boxShadow: "var(--shadow-md, 0 4px 12px rgba(0,0,0,0.08))",
            fontSize: 12,
            lineHeight: 1.4,
            color: "var(--text-primary)",
            fontFamily: "var(--font-sans)",
            verticalAlign: "baseline",
            textAlign: "left",
            whiteSpace: "normal",
            display: "block",
          }}
        >
          <span
            style={{
              display: "block",
              fontSize: 10,
              fontWeight: 600,
              color: "var(--text-tertiary)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: 4,
            }}
          >
            {POPOVER_LABELS[citation.sourceType] ?? citation.sourceType}
            {partition}
          </span>
          {summary ? (
            <span style={{ display: "block" }}>{summary}</span>
          ) : (
            <span style={{ display: "block", color: "var(--text-secondary)" }}>
              {truncatedId}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * Given a message text + ordered citation list, splice React nodes
 * (string segments + CitationChip components) so each citation is
 * rendered at its `(start, end)` offset in the text. Citations whose
 * offsets are out of range or overlap previous citations are dropped
 * — the parser SHOULD guarantee these constraints, but we are
 * defensive.
 *
 * The chip is rendered IMMEDIATELY AFTER the cited span (superscript
 * footnote convention). Plain text segments are emitted verbatim
 * between chips. The cited span itself is rendered in a subtle
 * underline so the user sees what the citation grounds.
 */
export function spliceCitationsIntoText(
  text: string,
  citations: Citation[],
  options: {
    onOpenSource?: (citation: Citation) => void;
    onOpenPage?: (citation: Citation) => void;
    /**
     * Resolver: optionally produce a URL for an external_object citation
     * (called once per chip during render). Returning null/undefined falls
     * back to the popover behaviour.
     */
    resolveUrl?: (citation: Citation) => string | null | undefined;
    /**
     * Resolver: optionally produce a popover summary for a citation.
     * Most callers leave this undefined and let the chip render its id.
     */
    resolveSummary?: (citation: Citation) => string | null | undefined;
  } = {},
): React.ReactNode[] {
  if (typeof text !== "string" || text.length === 0) return [];
  if (!Array.isArray(citations) || citations.length === 0) return [text];

  // Sort by `start` ascending. Drop entries with invalid ranges.
  const sorted = citations
    .filter(
      (c) =>
        Number.isFinite(c.start) &&
        Number.isFinite(c.end) &&
        c.start >= 0 &&
        c.end <= text.length &&
        c.end > c.start,
    )
    .sort((a, b) => a.start - b.start || a.end - b.end);

  if (sorted.length === 0) return [text];

  const out: React.ReactNode[] = [];
  let cursor = 0;
  let chipCounter = 0;

  for (const citation of sorted) {
    if (citation.start < cursor) {
      // Overlap with a previously emitted chip's cited span. Skip — the
      // earlier chip already covers this offset.
      continue;
    }
    chipCounter += 1;
    // Plain text before the cited span.
    if (citation.start > cursor) {
      out.push(text.slice(cursor, citation.start));
    }
    // The cited span itself, rendered with a subtle underline.
    const span = text.slice(citation.start, citation.end);
    out.push(
      <span
        key={`cited-${chipCounter}`}
        data-testid="citation-cited-span"
        style={{
          textDecoration: "underline",
          textDecorationColor: "var(--accent-muted)",
          textDecorationThickness: 1,
          textUnderlineOffset: 2,
        }}
      >
        {span}
      </span>,
    );
    // The chip immediately after the cited span.
    const url = options.resolveUrl ? options.resolveUrl(citation) ?? null : null;
    const summary = options.resolveSummary
      ? options.resolveSummary(citation) ?? null
      : null;
    out.push(
      <CitationChip
        key={`citation-${chipCounter}`}
        citation={citation}
        index={chipCounter}
        url={url}
        summary={summary}
        {...(options.onOpenSource ? { onOpenSource: options.onOpenSource } : {})}
        {...(options.onOpenPage ? { onOpenPage: options.onOpenPage } : {})}
      />,
    );
    cursor = citation.end;
  }

  // Trailing plain text.
  if (cursor < text.length) {
    out.push(text.slice(cursor));
  }

  return out;
}
